import { StreamingCsvParser, type Token } from '@databases/streaming-csv-parser';

import { createErrorClass } from '@canvas/error';
import { FailureReasons, hostNotifier } from '@canvas/host-notifier';

import type {
  DatabaseAiTeammateAnswerPartEvent,
  DatabaseAiTeammateRequest,
  DatabaseAiTeammateStreamEvent
} from './types.ts';

export const DATABASE_AI_TEAMMATE_ENDPOINT =
  '/gateway/api/assist/api/ai/v2/ai-feature/database/ai-teammate/stream';
export const DATABASE_AI_TEAMMATE_EXPERIENCE_ID = 'confluence-databases-ai-teammate';

export const DatabaseAiTeammateNetworkError = createErrorClass(
  'DatabaseAiTeammateNetworkError'
);
export const DatabaseAiTeammateServerError = createErrorClass(
  'DatabaseAiTeammateServerError'
);
export const DatabaseAiTeammateUnknownError = createErrorClass(
  'DatabaseAiTeammateUnknownError'
);
export const DatabaseAiTeammateBodyUndefinedError = createErrorClass(
  'DatabaseAiTeammateBodyUndefinedError'
);
export const DatabaseAiTeammateStreamParsingError = createErrorClass(
  'DatabaseAiTeammateStreamParsingError'
);

type StreamReader = ReadableStreamDefaultReader<Uint8Array>;

export type DatabaseAiTeammateTraceContext = Readonly<{
  traceId: string;
  spanId: string;
}>;

export type FetchDatabaseAiTeammateOptions = {
  input: DatabaseAiTeammateRequest;
  traceContext: DatabaseAiTeammateTraceContext;
  abortSignal?: AbortSignal;
};

/**
 * The type of database operation the AI teammate is performing. This is
 * forwarded to the streaming CSV parser so it can apply the correct parsing
 * rules for the operation (e.g. editing existing entries vs. creating new
 * ones).
 */
export type DatabaseAiTeammateOperationType = 'create' | 'edit';

export type DatabaseAiTeammateStreamProcessor = {
  onToken: (token: Token) => void;
  onStreamingEnd: () => void;
};

export async function fetchDatabaseAiTeammate({
  input,
  traceContext,
  abortSignal
}: FetchDatabaseAiTeammateOptions): Promise<StreamReader> {
  let response: Response;

  try {
    response = await fetch(DATABASE_AI_TEAMMATE_ENDPOINT, {
      body: JSON.stringify({
        ai_feature_input: {
          prompt: input.prompt,
          locale: input.locale,
          database_context: input.databaseContext
        }
      }),
      headers: {
        'X-Product': 'confluence',
        'X-Experience-Id': DATABASE_AI_TEAMMATE_EXPERIENCE_ID,
        'X-B3-TraceId': traceContext.traceId,
        'X-B3-SpanId': traceContext.spanId,
        'content-type': 'application/json'
      },
      method: 'POST',
      signal: abortSignal
    });
  } catch (error) {
    reportExternalFailure();
    throw new DatabaseAiTeammateNetworkError(
      'Network error occurred when fetching from the database ai-teammate API',
      undefined,
      error instanceof Error ? error : undefined
    );
  }

  if (response.status >= 500) {
    reportExternalFailure();
    throw new DatabaseAiTeammateServerError(
      'Server error occurred when fetching from the database ai-teammate API'
    );
  }

  if (!response.ok) {
    reportExternalFailure();
    throw new DatabaseAiTeammateUnknownError(
      'An unknown error occurred when fetching from the database ai-teammate API'
    );
  }

  if (!response.body) {
    throw new DatabaseAiTeammateBodyUndefinedError(
      'Undefined body received when fetching from the database ai-teammate API'
    );
  }

  return response.body.getReader();
}

export async function* iterateDatabaseAiTeammateDocumentParts(
  reader: StreamReader
): AsyncGenerator<string> {
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const documentPart = parseDocumentPart(line);

      if (documentPart !== undefined) {
        yield documentPart;
      }
    }
  }

  buffer += decoder.decode();

  if (buffer.length > 0) {
    const documentPart = parseDocumentPart(buffer);

    if (documentPart !== undefined) {
      yield documentPart;
    }
  }
}

export async function processDatabaseAiTeammateStream(
  reader: StreamReader,
  operationType: DatabaseAiTeammateOperationType,
  processor: DatabaseAiTeammateStreamProcessor
): Promise<void> {
  const parser = new StreamingCsvParser((token) => {
    processor.onToken(token);
  }, operationType);

  for await (const documentPart of iterateDatabaseAiTeammateDocumentParts(reader)) {
    parser.onReceiveChunk(documentPart);
  }

  parser.onReceiveChunk('\n');
  processor.onStreamingEnd();
}

function parseDocumentPart(line: string): string | undefined {
  const trimmedLine = line.trim();

  if (trimmedLine.length === 0) {
    return undefined;
  }

  let event: DatabaseAiTeammateStreamEvent;

  try {
    event = JSON.parse(trimmedLine);
  } catch (error) {
    throw new DatabaseAiTeammateStreamParsingError(
      'Unable to parse database ai-teammate stream event',
      undefined,
      error instanceof Error ? error : undefined
    );
  }

  if (!isAnswerPartEvent(event)) {
    return undefined;
  }

  return event.message.document_part;
}

function isAnswerPartEvent(
  event: DatabaseAiTeammateStreamEvent
): event is DatabaseAiTeammateAnswerPartEvent {
  return (
    event.type === 'ANSWER_PART' &&
    typeof event.message === 'object' &&
    event.message !== null &&
    'document_part' in event.message &&
    typeof event.message.document_part === 'string'
  );
}

function reportExternalFailure() {
  hostNotifier.reportExternalFailure({
    source: FailureReasons.AI_TEAMMATE_API_FAILURE,
    location: 'database-ai-teammate-client',
    endpoint: DATABASE_AI_TEAMMATE_ENDPOINT
  });
}
